// 브라우저용 로거 - winston 대신 네이티브 console 사용
class BrowserLogger {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = (import.meta as any).env?.DEV || false;
  }

  private formatMessage(level: string, message: string, meta?: any): string {
    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 23);
    return `${timestamp} | ${level.toUpperCase().padEnd(7)} | ${message}`;
  }

  private getColor(level: string): string {
    const colors: { [key: string]: string } = {
      error: '#f87171',   // red-400
      warn: '#fbbf24',    // yellow-400  
      info: '#60a5fa',    // blue-400
      debug: '#a3a3a3',   // gray-400
    };
    return colors[level] || colors.info;
  }

  private log(level: string, message: string, meta?: any): void {
    const formattedMessage = this.formatMessage(level, message);
    const color = this.getColor(level);
    
    if (meta && Object.keys(meta).length > 0) {
      console.log(
        `%c${formattedMessage}`,
        `color: ${color}`,
        meta
      );
    } else {
      console.log(
        `%c${formattedMessage}`,
        `color: ${color}`
      );
    }
  }

  info(message: string, meta?: any): void {
    this.log('info', message, meta);
  }
  
  warn(message: string, meta?: any): void {
    this.log('warn', message, meta);
  }
  
  error(message: string, error?: Error | any): void {
    if (error instanceof Error) {
      this.log('error', message, { 
        error: error.message,
        stack: error.stack,
        name: error.name
      });
    } else {
      this.log('error', message, { error });
    }
  }
  
  debug(message: string, meta?: any): void {
    if (this.isDevelopment) {
      this.log('debug', message, meta);
    }
  }

  // API 호출 로깅을 위한 헬퍼
  apiCall(method: string, url: string, status?: number, duration?: number): void {
    const meta = { method, url, status, duration };
    if (status && status >= 400) {
      this.error(`API ${method} ${url} failed`, meta);
    } else {
      this.info(`API ${method} ${url}`, meta);
    }
  }

  // 컴포넌트 생명주기 로깅
  component(componentName: string, action: string, meta?: any): void {
    this.debug(`[${componentName}] ${action}`, meta);
  }
}

// 로거 인스턴스 생성
const logger = new BrowserLogger();

// 로거 인터페이스 - 백엔드의 loguru와 유사한 API 제공
export const log = {
  info: (message: string, meta?: any) => {
    logger.info(message, meta);
  },
  
  warn: (message: string, meta?: any) => {
    logger.warn(message, meta);
  },
  
  error: (message: string, error?: Error | any) => {
    logger.error(message, error);
  },
  
  debug: (message: string, meta?: any) => {
    logger.debug(message, meta);
  },

  // API 호출 로깅을 위한 헬퍼
  apiCall: (method: string, url: string, status?: number, duration?: number) => {
    logger.apiCall(method, url, status, duration);
  },

  // 컴포넌트 생명주기 로깅
  component: (componentName: string, action: string, meta?: any) => {
    logger.component(componentName, action, meta);
  }
};

export default logger;